import { Action, createReducer, on } from '@ngrx/store';
import * as fromActions from './tasks.actions';
import { AuditorTask } from '../../models/AuditorTask';
import { groupTasks, addTaskToGroups } from './tasks.utils';
import { AvailableAuditorTask } from '../../models/AvailableAuditorTask';
import { HttpErrorResponse } from '@angular/common/http';

export interface TasksState {
  loadingAssignedTasks: boolean;
  loadingAvailableTasks: boolean;
  loadingAuditorState: boolean;
  error: string;
  actionError: string | null;
  tasks: AuditorTask[];
  shouldNotify: boolean;
  lastKnownShouldNotify: boolean;
  isAuditorReady: boolean;
  lastKnownIsAuditorReady: boolean;
  auditedFlagged: number;
  requiredFlagged: number;
  availableTasks: {
    periodId: string;
    branchId: number;
    group: string;
    items: AvailableAuditorTask[];
  }[];
  takingTaskIds: number[];
  loadedAvailableTasks: number;
}

export const initialTasksState: TasksState = {
  loadingAssignedTasks: true,
  loadingAvailableTasks: true,
  loadingAuditorState: true,
  error: null,
  actionError: null,
  shouldNotify: false,
  lastKnownShouldNotify: false,
  isAuditorReady: false,
  lastKnownIsAuditorReady: false,
  auditedFlagged: 0,
  requiredFlagged: 0,
  tasks: [],
  availableTasks: [],
  takingTaskIds: [],
  loadedAvailableTasks: 2,
};

const reducer = createReducer(
  initialTasksState,
  on(fromActions.initPage, state => (state = { ...initialTasksState })),
  on(fromActions.loadAssignedTasksSuccess, (state, { tasks }): TasksState =>
    ({ ...state, tasks, loadingAssignedTasks: false })),
  on(fromActions.loadAvailableTasksSuccess, (state, { availableTasks }): TasksState =>
    ({ ...state, availableTasks: groupTasks(availableTasks), loadingAvailableTasks: false })),
  on(fromActions.loadAuditorStateSuccess, (state, { notifyInfo }): TasksState => ({
    ...state,
    loadingAuditorState: false,
    shouldNotify: notifyInfo.shouldNotify,
    lastKnownShouldNotify: notifyInfo.shouldNotify,
    isAuditorReady: notifyInfo.isAvailable,
    lastKnownIsAuditorReady: notifyInfo.isAvailable,
    auditedFlagged: notifyInfo.auditedFlagged,
    requiredFlagged: notifyInfo.requiredFlagged,
  })),
  on(fromActions.loadTasksError, (state, { error }): TasksState => ({
    ...state,
    loadingAssignedTasks: false,
    loadingAvailableTasks: false,
    loadingAuditorState: false,
    error: error.message,
  })),
  on(fromActions.setShouldNotify, (state, { shouldNotify }) => (state = { ...state, shouldNotify })),
  on(
    fromActions.setShouldNotifySuccess,
    (state, { newShouldNotify }) =>
      (state = {
        ...state,
        shouldNotify: newShouldNotify,
        lastKnownShouldNotify: newShouldNotify,
      }),
  ),
  on(fromActions.setShouldNotifyFailed, state => (state = { ...state, shouldNotify: state.lastKnownShouldNotify })),
  on(fromActions.setIsAuditorReady, (state, { isAuditorReady }) => (state = { ...state, isAuditorReady })),
  on(
    fromActions.setIsAuditorReadySuccess,
    (state, { newIsAuditorReady }) =>
      (state = {
        ...state,
        isAuditorReady: newIsAuditorReady,
        lastKnownIsAuditorReady: newIsAuditorReady,
      }),
  ),
  on(fromActions.setIsAuditorReadyFailed, state => (state = { ...state, isAuditorReady: state.lastKnownIsAuditorReady })),
  on(fromActions.takeTask, (state, { task }) => ({ ...state, actionError: null as string | null, takingTaskIds: [task.id] })),
  on(
    fromActions.takeTaskSuccess,
    (state, { task, assigneeUsername }) =>
      (state = {
        ...state,
        tasks: [...state.tasks, { ...task }],
        availableTasks: state.availableTasks.map(group => ({
          ...group,
          items: group.items.map(t => (t.id === task.id ? { ...task, taken: true, assignedTo: assigneeUsername } : t)),
        })),
        takingTaskIds: state.takingTaskIds.filter(id => id !== task.id),
      }),
  ),
  on(
    fromActions.takeTaskFailed,
    (state, { error }) =>
      (state = {
        ...state,
        actionError: 'error' in error ? (error as HttpErrorResponse).error : error.message,
        takingTaskIds: [] as number[],
      }),
  ),
  on(fromActions.takeAllTasks, (state, { tasks }) => ({
    ...state,
    actionError: null as string | null,
    takingTaskIds: tasks.map(task => task.id),
  })),
  on(
    fromActions.takeAllTasksSuccess,
    (state, { tasks, assigneeUsername }) =>
      (state = {
        ...state,
        tasks: [...state.tasks, ...tasks.map(task => ({ ...task }))],
        availableTasks: state.availableTasks.map(group => ({
          ...group,
          items: group.items.map(t => {
            const taken = tasks.find(task => task.id === t.id);
            return taken ? { ...taken, taken: true, assignedTo: assigneeUsername } : t;
          }),
        })),
        takingTaskIds: [] as number[],
      }),
  ),
  on(
    fromActions.takeAllTasksFailed,
    (state, { error }) =>
      (state = {
        ...state,
        actionError: 'error' in error ? (error as HttpErrorResponse).error : error.message,
        takingTaskIds: [] as number[],
      }),
  ),
  on(
    fromActions.giveUpTaskSuccess,
    (state, { task }) =>
      (state = {
        ...state,
        tasks: state.tasks.filter(t => t.id !== task.id),
        availableTasks: addTaskToGroups(state.availableTasks, task),
      }),
  ),
  on(
    fromActions.loadMoreAvailableTasks,
    state =>
      (state = {
        ...state,
        loadedAvailableTasks: Math.min(state.availableTasks.length, state.loadedAvailableTasks + 1),
      }),
  ),
);

export function tasksReducer(state: TasksState, action: Action) {
  return reducer(state, action);
}
